import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast } from 'react-toastify';

import { FolderIcon } from '@/shared/assets/icons/folder-icon';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { FormInput } from '@/shared/components/ui/form/components/form-input/form-input';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import { Form } from '@/shared/components/ui/form/form';
import {
  FormContainer,
  FormContent,
} from '@/shared/components/ui/form/form.styled';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { Trigger } from '@/shared/components/ui/trigger/trigger';
import {
  CreateResourceGroupInputSchemaType,
  TCreateGroupProps,
} from '@/shared/features/groups/components/create-group/create-group.types';
import { createResourceGroupInputSchema } from '@/shared/features/groups/components/create-group/create-group.validation';
import { UNGROUPED_RESOURCES_GROUP_ID } from '@/shared/features/groups/components/group-list/group-list.constants';
import { GroupSelect } from '@/shared/features/groups/components/group-select/group-select';
import { useGroupsConfig } from '@/shared/features/groups/context/groups-config-context';
import { useGroupScopedAction } from '@/shared/features/groups/hooks/use-group-scoped-action/use-group-scoped-action';
import { Can } from '@/shared/features/permissions/components/can/can';
import { useCreateResourceGroupMutation } from '@/shared/redux/api/assets/groups/groups-api';
import { showToastError } from '@/shared/utils/toast';

export const CreateGroup: React.FC<TCreateGroupProps> = ({
  handlePopoverClose,
  groupId,
  component = 'MenuItem',
  label,
  icon,
}) => {
  const { t } = useTranslation();
  const { groupId: currentGroupId } = useParams();
  const { createGroupPermission, permissionActions } = useGroupsConfig();
  const [createResourceGroup] = useCreateResourceGroupMutation();
  const _label = label ?? t('groups:drawer.create_group.title');
  const _icon = icon ?? <FolderIcon variant="primary" />;

  // Pre-fill the parent with the current group (prop wins; never the ungrouped id).
  const getParentGroupId = () => {
    if (groupId && groupId !== UNGROUPED_RESOURCES_GROUP_ID) {
      return groupId;
    }
    if (currentGroupId && currentGroupId !== UNGROUPED_RESOURCES_GROUP_ID) {
      return currentGroupId;
    }
    return undefined;
  };
  const parentGroupId = getParentGroupId();
  const groupScope = useGroupScopedAction({
    action: permissionActions.createGroup,
    groupId: parentGroupId,
  });
  const formSchema = createResourceGroupInputSchema(groupScope.requiresGroup);
  const drawerPermission = parentGroupId
    ? groupScope.permission
    : createGroupPermission;

  const handleSubmit = async (
    data: TSubmittedData<CreateResourceGroupInputSchemaType>,
    handleClose: () => void,
  ) => {
    try {
      await createResourceGroup({ resourceGroup: data.data }).unwrap();
      toast.success(t('groups:drawer.create_group.create_success'));
      handleClose();
      handlePopoverClose?.();
    } catch {
      showToastError(t('groups:drawer.create_group.create_error'));
    }
  };

  return (
    <Can action={permissionActions.createGroup} ids={groupScope.groupId}>
      <DrawerController
        trigger={
          <Trigger
            component={component}
            icon={_icon}
            label={_label}
            buttonSize="medium"
          />
        }
        title={t('groups:drawer.create_group.title')}
        description={t('groups:drawer.create_group.subtitle')}
        width="500px"
        permission={drawerPermission}
      >
        {(handleClose) => (
          <Form<CreateResourceGroupInputSchemaType>
            onSubmit={(event) => handleSubmit(event, handleClose)}
            schema={formSchema}
          >
            {({
              formState,
            }: UseFormReturn<CreateResourceGroupInputSchemaType>) => (
              <FormContainer justifyContent="space-between">
                <FormContent overflow="auto">
                  <GroupSelect<CreateResourceGroupInputSchemaType>
                    name="parentGroupId"
                    parentGroupId={groupScope.groupId}
                    getItemId={(value) => value}
                    {...groupScope.groupSelectProps}
                    disabled={formState.isSubmitting}
                  />
                  <FormInput<CreateResourceGroupInputSchemaType>
                    name="name"
                    label={t('groups:drawer.create_group.group_name.label')}
                    required
                  />
                </FormContent>
                <Submit
                  onClose={(event) => {
                    event.stopPropagation();
                    handleClose();
                    handlePopoverClose?.();
                  }}
                />
              </FormContainer>
            )}
          </Form>
        )}
      </DrawerController>
    </Can>
  );
};
